/**
 * Service for managing template data
 *
 * This service provides methods for loading, validating, and filtering
 * musical composition templates. It loads templates from JSON on initialization
 * and provides various query methods for accessing template data.
 *
 * @module services/templates/TemplateService
 *
 * @example
 * ```typescript
 * import { templateService } from './services/templates/TemplateService';
 *
 * // Get all templates
 * const allTemplates = templateService.getAllTemplates();
 *
 * // Search for templates
 * const battleTemplates = templateService.searchTemplates('battle');
 *
 * // Filter with multiple criteria
 * const filtered = templateService.filterTemplates({
 *   search: 'fast',
 *   categories: ['battle'],
 *   difficulties: ['beginner']
 * });
 * ```
 */
import type { Template, TemplateCategory, TemplateDifficulty, TemplateFilters, } from '../../types/template';
import templatesData from './templates.json';
/**
 * Service class for accessing and filtering template data
 *
 * Singleton service that manages the template library.
 * Automatically loads and validates templates on instantiation.
 */
class TemplateService {
    private templates: Template[];
    private loadError: Error | null = null;
    constructor() {
        this.templates = this.loadTemplates();
    }
    /**
     * Load and validate templates from JSON
     *
     * Loads template data from the JSON file and validates each template.
     * Invalid templates are filtered out with warnings logged to console.
     *
     * @returns Array of valid templates
     * @private
     */
    private loadTemplates(): Template[] {
        try {
            // Check if templates data exists
            if (!templatesData || !Array.isArray(templatesData)) {
                const error = new Error('Template data is missing or invalid');
                console.error('[TemplateService] Failed to load templates:', error);
                this.loadError = error;
                return [];
            }
            // Validate each template
            const validTemplates = (templatesData as Template[]).filter((template) => this.validateTemplate(template));
            // Warn if no templates loaded successfully
            if (validTemplates.length === 0) {
                const warning = new Error('No valid templates found');
                console.warn('[TemplateService] Warning:', warning.message);
                this.loadError = warning;
            }
            else {
            }
            return validTemplates;
        }
        catch (error) {
            console.error('[TemplateService] Failed to load templates:', error);
            this.loadError = error instanceof Error ? error : new Error('Unknown error loading templates');
            return [];
        }
    }
    /**
     * Check if templates loaded successfully
     *
     * @returns True if there was an error loading templates
     *
     * @example
     * ```typescript
     * if (templateService.hasLoadError()) {
     *   console.error('Failed to load templates');
     * }
     * ```
     */
    hasLoadError(): boolean {
        return this.loadError !== null;
    }
    /**
     * Get load error if any
     *
     * @returns Error object if loading failed, null otherwise
     *
     * @example
     * ```typescript
     * const error = templateService.getLoadError();
     * if (error) {
     *   console.error('Template load error:', error.message);
     * }
     * ```
     */
    getLoadError(): Error | null {
        return this.loadError;
    }
    /**
     * Validate template structure
     *
     * Checks that a template has all required fields with correct types.
     * Logs warnings for invalid templates.
     *
     * @param template - Template object to validate
     * @returns True if template is valid, false otherwise
     * @private
     */
    private validateTemplate(template: any): boolean {
        // Check required fields
        if (!template.id || typeof template.id !== 'string') {
            return false;
        }
        if (!template.name || typeof template.name !== 'string') {
            return false;
        }
        if (!template.composition || typeof template.composition !== 'object') {
            return false;
        }
        if (!template.difficulty) {
            return false;
        }
        if (!Array.isArray(template.category) || template.category.length === 0) {
            return false;
        }
        return true;
    }
    /**
     * Get all templates
     *
     * @returns Array of all loaded templates
     *
     * @example
     * ```typescript
     * const templates = templateService.getAllTemplates();
     * console.log(`Loaded ${templates.length} templates`);
     * ```
     */
    getAllTemplates(): Template[] {
        return this.templates;
    }
    /**
     * Get template by ID
     *
     * @param id - Unique template identifier
     * @returns Template if found, undefined otherwise
     *
     * @example
     * ```typescript
     * const template = templateService.getTemplateById('battle-pixel-warrior');
     * if (template) {
     *   console.log(`Found: ${template.name}`);
     * }
     * ```
     */
    getTemplateById(id: string): Template | undefined {
        return this.templates.find((t) => t.id === id);
    }
    /**
     * Get templates by category
     *
     * Returns all templates that include the specified category.
     * Templates can belong to multiple categories.
     *
     * @param category - Category to filter by
     * @returns Array of templates in the specified category
     *
     * @example
     * ```typescript
     * const battleTemplates = templateService.getTemplatesByCategory('battle');
     * console.log(`Found ${battleTemplates.length} battle templates`);
     * ```
     */
    getTemplatesByCategory(category: TemplateCategory): Template[] {
        return this.templates.filter((t) => t.category.includes(category));
    }
    /**
     * Get templates by difficulty
     *
     * @param difficulty - Difficulty level to filter by
     * @returns Array of templates at the specified difficulty level
     *
     * @example
     * ```typescript
     * const beginnerTemplates = templateService.getTemplatesByDifficulty('beginner');
     * console.log(`Found ${beginnerTemplates.length} beginner templates`);
     * ```
     */
    getTemplatesByDifficulty(difficulty: TemplateDifficulty): Template[] {
        return this.templates.filter((t) => t.difficulty === difficulty);
    }
    /**
     * Search templates by query
     *
     * Performs case-insensitive search across template name, description, and tags.
     *
     * @param query - Search query string
     * @returns Array of templates matching the query
     *
     * @example
     * ```typescript
     * const results = templateService.searchTemplates('fast battle');
     * console.log(`Found ${results.length} matching templates`);
     * ```
     */
    searchTemplates(query: string): Template[] {
        const lowerQuery = query.toLowerCase();
        return this.templates.filter((t) => t.name.toLowerCase().includes(lowerQuery) ||
            t.description.toLowerCase().includes(lowerQuery) ||
            t.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)));
    }
    /**
     * Filter templates based on multiple criteria
     *
     * Applies search, category, and difficulty filters simultaneously.
     * Optimized to perform all filters in a single pass for better performance.
     * Empty filter arrays mean no filter is applied for that criterion.
     *
     * @param filters - Filter criteria to apply
     * @returns Array of templates matching all specified criteria
     *
     * @example
     * ```typescript
     * // Filter for beginner battle templates containing "fast"
     * const filtered = templateService.filterTemplates({
     *   search: 'fast',
     *   categories: ['battle'],
     *   difficulties: ['beginner']
     * });
     *
     * // No filters - returns all templates
     * const all = templateService.filterTemplates({
     *   search: '',
     *   categories: [],
     *   difficulties: []
     * });
     * ```
     */
    filterTemplates(filters: TemplateFilters): Template[] {
        // Early return if no filters applied
        if (!filters.search && filters.categories.length === 0 && filters.difficulties.length === 0) {
            return this.templates;
        }
        const lowerQuery = filters.search ? filters.search.toLowerCase() : '';
        const hasSearch = !!filters.search;
        const hasCategories = filters.categories.length > 0;
        const hasDifficulties = filters.difficulties.length > 0;
        // Single-pass filter for optimal performance
        return this.templates.filter((template) => {
            // Apply search filter
            if (hasSearch) {
                const matchesSearch = template.name.toLowerCase().includes(lowerQuery) ||
                    template.description.toLowerCase().includes(lowerQuery) ||
                    template.tags.some((tag) => tag.toLowerCase().includes(lowerQuery));
                if (!matchesSearch)
                    return false;
            }
            // Apply category filter
            if (hasCategories) {
                const matchesCategory = filters.categories.some((cat) => template.category.includes(cat));
                if (!matchesCategory)
                    return false;
            }
            // Apply difficulty filter
            if (hasDifficulties) {
                if (!filters.difficulties.includes(template.difficulty))
                    return false;
            }
            return true;
        });
    }
    /**
     * Get category counts
     *
     * Returns the number of templates in each category.
     * Useful for displaying category statistics in the UI.
     *
     * @returns Object mapping each category to its template count
     *
     * @example
     * ```typescript
     * const counts = templateService.getCategoryCounts();
     * console.log(`Battle templates: ${counts.battle || 0}`);
     * console.log(`Victory templates: ${counts.victory || 0}`);
     * ```
     */
    getCategoryCounts(): Record<TemplateCategory, number> {
        const counts = {} as Record<TemplateCategory, number>;
        this.templates.forEach((template) => {
            template.category.forEach((cat) => {
                counts[cat] = (counts[cat] || 0) + 1;
            });
        });
        return counts;
    }
}
// Export singleton instance
export const templateService = new TemplateService();
